// assets/somm-attendee-form.js
//
// SOMM guest-intake form. Pairs with blocks/somm-attendee-form.liquid.
//
// Each guest becomes its own quantity-one cart line (PLAN.md:45-46). That is
// why every line carries a unique _somm_ticket_id: Shopify merges cart lines
// whose variant AND properties are identical, so without a per-ticket unique
// value two identically-filled tickets would silently collapse into one line
// of quantity 2. The ticket id is what keeps them distinct.
//
// The ticket id is browser-generated and therefore customer-controlled. It is
// a correlation hint only and must never be treated as authorization
// (PLAN.md:105, PLAN.md:107).
//
// N9 (2026-09-21): simplified from the original ported form. Dropped the
// six-ticket-per-order cap (inventory is now the only cap -- PLAN.md's Sept 9
// owner decision), the "unknown guest" toggle, and the required-email +
// allergy-radio fields. Three fields per guest now: name (required), dietary
// (optional), contact (optional). Properties are visible by default --
// `Guest {n} name` / `Guest {n} contact` -- except dietary, which stays
// underscore-hidden (`_Guest {n} dietary`) per the architect's PLAN.md:153
// resolution: Shopify hides underscore-prefixed properties from
// cart/checkout/notification emails while keeping them on the order for
// Admin staff and the Worker webhook.

import { Component } from '@theme/component';
// Ported from Horizon 3.5.1 to 4.1.3 on 2026-08-10.
//
// 3.5.1 exported CartAddEvent and CartErrorEvent from '@theme/events'. 4.1.3
// removed both from that module and moved the cart event model to the
// platform: CartAddEvent no longer exists, CartLinesUpdateEvent replaces it,
// and CartErrorEvent changed from a plain Event with positional arguments to
// a ShopifyStandardEvent taking a single options object.
//
// The lifecycle also inverted. 3.5.1 dispatched CartAddEvent *after* a
// successful add, carrying the result. 4.1.3 dispatches CartLinesUpdateEvent
// *before* the request with a deferred promise the dispatcher resolves once
// the cart is re-read. Both dispatch sites below follow the 4.1.3 pattern in
// product-form.js.
//
// Note for anyone changing this: the partial-add safety check does not
// depend on these events. It re-reads the cart and compares ticket IDs
// directly, so a mistake in the event wiring degrades the cart UI, not the
// guarantee that no guest is silently lost.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { buildGuestProperties, validateGuestTickets } from './somm-attendee-form-helpers.js';

/** Wire-format version for the line-item property schema. Bump on any change. */
const SCHEMA_VERSION = '2';

/**
 * One guest's live form state.
 * @typedef {object} SommTicket
 * @property {number} number - 1-based ticket number within this order.
 * @property {HTMLElement} section - The fieldset this record was read from.
 * @property {string} name
 * @property {string} dietary
 * @property {string} contact
 */

/**
 * @typedef {object} SommAttendeeFormRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLSelectElement} [ticketCount]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLButtonElement} [cartButton]
 * @property {HTMLElement} [formSuccess]
 * @property {HTMLElement} [formError]
 * @property {HTMLElement} [liveRegion]
 */

/**
 * @extends Component<SommAttendeeFormRefs>
 */
class SommAttendeeForm extends Component {
  #submitting = false;
  #redirecting = false;

  /** @type {?(event: PageTransitionEvent) => void} */
  #onPageShow = null;

  connectedCallback() {
    super.connectedCallback();

    // The block renders nothing when the event is sold out or when the cart
    // already holds every remaining ticket. In those cases there is no form
    // to wire up and the component is inert by design.
    if (!this.refs.form) return;

    this.#syncAllTickets();

    // The rendered allowance can already be stale on arrival: a page
    // restored from bfcache, or reached with the back button after checking
    // out in another tab, shows counts from whenever it was rendered.
    // Re-read once on connect so the form starts from the live cart rather
    // than a memory of it.
    this.#refreshAllowance();

    // Same reason, for a genuine bfcache restore, which does not re-run
    // connectedCallback.
    this.#onPageShow = (event) => {
      if (!event.persisted) return;
      // Back from checkout: the page comes back exactly as it was left,
      // buttons disabled mid-redirect. Re-enable them, then re-read the cart.
      if (this.#redirecting) {
        this.#redirecting = false;
        this.#setSubmitting(false);
      }
      this.#refreshAllowance();
    };
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onPageShow) window.removeEventListener('pageshow', this.#onPageShow);
  }

  // -------------------------------------------------------------- handlers

  handleTicketCountChange() {
    this.#syncAllTickets();
    this.#clearFormError();
  }

  /** @param {SubmitEvent} event */
  handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;

    this.#clearAllErrors();

    const tickets = this.#collectTickets();
    const errors = this.#validate(tickets);

    if (errors.length > 0) {
      this.#renderErrors(errors);
      return;
    }

    // N10 (2026-09-25): the primary button ("Buy tickets") goes straight to
    // checkout after a confirmed add; the secondary "Add to cart and keep
    // shopping" button keeps the buyer on the page and opens the cart drawer.
    const submitter = /** @type {?HTMLElement} */ (event.submitter ?? null);
    const mode = submitter?.dataset.sommMode === 'cart' ? 'cart' : 'checkout';

    this.#addToCart(tickets, mode);
  }

  // ----------------------------------------------------------- form state

  get #selectedCount() {
    const { ticketCount } = this.refs;
    return ticketCount ? Number(ticketCount.value) : 1;
  }

  /** @returns {HTMLElement[]} */
  get #ticketSections() {
    return Array.from(this.querySelectorAll('[data-somm-ticket]'));
  }

  #syncAllTickets() {
    for (const section of this.#ticketSections) this.#syncTicket(section);
  }

  /**
   * Single source of truth for a ticket section's visibility and whether its
   * inputs are live.
   *
   * Disabling is not cosmetic. A disabled input is excluded from validation
   * and from collection, which is what stops "hidden on screen" and
   * "not submitted" from drifting apart as the form is toggled around.
   *
   * @param {HTMLElement} section
   */
  #syncTicket(section) {
    const number = Number(section.dataset.sommTicket);
    const active = number <= this.#selectedCount;

    section.hidden = !active;

    for (const field of section.querySelectorAll('input[type="text"], textarea')) {
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        field.disabled = !active;
      }
    }
  }

  // ------------------------------------------------------------ collection

  /**
   * Reads the live form state into one record per ticket.
   * @returns {SommTicket[]}
   */
  #collectTickets() {
    return this.#ticketSections
      .filter((section) => !section.hidden)
      .map((section) => {
        const name = /** @type {HTMLInputElement | null} */ (
          section.querySelector('input[name*="[name]"]')
        );
        const dietary = /** @type {HTMLTextAreaElement | null} */ (
          section.querySelector('textarea[name*="[dietary]"]')
        );
        const contact = /** @type {HTMLInputElement | null} */ (
          section.querySelector('input[name*="[contact]"]')
        );

        return {
          number: Number(section.dataset.sommTicket),
          section,
          name: name?.value.trim() ?? '',
          dietary: dietary?.value.trim() ?? '',
          contact: contact?.value.trim() ?? '',
        };
      });
  }

  // ------------------------------------------------------------ validation

  /**
   * @param {SommTicket[]} tickets
   * @returns {Array<{section: HTMLElement, selector: string, message: string}>}
   */
  #validate(tickets) {
    return validateGuestTickets(tickets).map((error) => {
      const ticket = tickets.find((t) => t.number === error.number);
      return {
        section: /** @type {HTMLElement} */ (ticket?.section),
        selector: 'input[name*="[name]"]',
        message: error.message,
      };
    });
  }

  /** @param {Array<{section: HTMLElement, selector: string, message: string}>} errors */
  #renderErrors(errors) {
    for (const error of errors) {
      const target = error.section.querySelector(error.selector);
      if (!target) continue;

      const errorEl = target.parentElement?.querySelector('[data-somm-role="error"]');

      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }

      if (target instanceof HTMLElement) target.setAttribute('aria-invalid', 'true');
    }

    const first = errors[0];
    const focusTarget = first?.section.querySelector(first.selector);
    if (focusTarget instanceof HTMLElement) focusTarget.focus();

    this.#announce(`${errors.length} field${errors.length === 1 ? '' : 's'} need attention.`);
  }

  #clearAllErrors() {
    for (const el of this.querySelectorAll('[data-somm-role="error"]')) {
      if (el instanceof HTMLElement) {
        el.textContent = '';
        el.hidden = true;
      }
    }
    for (const el of this.querySelectorAll('[aria-invalid]')) {
      el.removeAttribute('aria-invalid');
    }
    this.#clearFormError();
  }

  // --------------------------------------------------------------- cart add

  /**
   * Builds the line-item properties for one ticket.
   *
   * Guest name and contact are ordinary (visible) properties -- the buyer
   * sees them in cart, which is how "verify cart shows the visible
   * properties" is checked. Dietary detail is the one underscore-hidden
   * property: Shopify keeps `_`-prefixed keys out of standard customer-
   * facing surfaces (cart, checkout, notification emails) while leaving them
   * readable in Admin and to the Worker webhook -- that behavior is the
   * access control PLAN.md:153 requires, not a separate roster system.
   *
   * Empty values are omitted, because Shopify drops line-item properties
   * whose value is an empty string.
   *
   * @param {SommTicket} ticket
   * @returns {Record<string, string>}
   */
  #buildProperties(ticket) {
    const form = this.refs.form;
    if (!form) return {};

    /** @type {Record<string, string>} */
    const properties = {
      _somm_schema: SCHEMA_VERSION,
      _somm_ticket_id: this.#ticketId(),
      _somm_event_product_id: form.dataset.productId ?? '',
      ...buildGuestProperties(ticket),
    };

    // Correlation and display snapshots only. Never authorization
    // (PLAN.md:105).
    if (form.dataset.eventTitle) properties._somm_event_title = form.dataset.eventTitle;
    if (form.dataset.eventStart) properties._somm_event_start = form.dataset.eventStart;

    return properties;
  }

  /** @returns {string} */
  #ticketId() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

    // Fallback for browsers without randomUUID. Uniqueness here only has to
    // stop Shopify from merging two cart lines; it carries no security
    // weight.
    const random = Math.random().toString(36).slice(2);
    return `t-${Date.now().toString(36)}-${random}`;
  }

  // --------------------------------------------------------- cart allowance

  /**
   * Fetches the live cart.
   *
   * @returns {Promise<?{items: Array<Object>}>} Null if the cart cannot be read.
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Counts tickets already in the cart for THIS event.
   *
   * Counted by product ID, not variant, so a mixed cart of ordinary
   * merchandise plus this event's tickets is unaffected, and so a ticket
   * added before this block existed still occupies inventory.
   *
   * @param {{items: Array<Object>}} cart
   * @returns {number}
   */
  #countInCart(cart) {
    const form = this.refs.form;
    if (!form || !cart?.items) return 0;

    const productId = String(form.dataset.productId);

    return cart.items.reduce((total, item) => {
      const matches =
        String(item.product_id) === productId ||
        String(item.properties?._somm_event_product_id ?? '') === productId;
      return matches ? total + (item.quantity || 0) : total;
    }, 0);
  }

  /**
   * Recomputes how many more tickets may be selected.
   *
   * Inventory is a render-time snapshot; it can only tighten the allowance,
   * never widen it. If stock moved since render, the server rejects the add
   * and the error surfaces -- inventory is Shopify's to enforce, not ours.
   * Untracked inventory has no real cap, so the allowance is bounded only by
   * the rendering ceiling.
   *
   * @param {{items: Array<Object>}} cart
   * @returns {number}
   */
  #computeAllowance(cart) {
    const form = this.refs.form;
    if (!form) return 0;

    const renderCeiling = Number(form.dataset.renderCeiling) || 0;
    let allowance = renderCeiling;

    if (form.dataset.inventoryTracked === 'true') {
      const inventoryQuantity = Number(form.dataset.inventoryQuantity) || 0;
      allowance = Math.min(inventoryQuantity - this.#countInCart(cart), renderCeiling);
    }

    return Math.max(allowance, 0);
  }

  /**
   * Rebuilds the ticket-count options to match a recomputed allowance,
   * keeping the buyer's current choice where it is still valid.
   *
   * @param {number} allowance
   */
  #applyAllowance(allowance) {
    const { ticketCount, submitButton, cartButton, form } = this.refs;
    if (!ticketCount || !form) return;

    form.dataset.maxSelectable = String(allowance);

    if (allowance <= 0) {
      ticketCount.innerHTML = '';
      ticketCount.disabled = true;
      if (submitButton) submitButton.disabled = true;
      if (cartButton) cartButton.disabled = true;
      this.#syncAllTickets();
      return;
    }

    const desired = Math.min(this.#selectedCount || 1, allowance);

    ticketCount.innerHTML = '';
    for (let i = 1; i <= allowance; i++) {
      const option = document.createElement('option');
      option.value = String(i);
      option.textContent = String(i);
      if (i === desired) option.selected = true;
      ticketCount.appendChild(option);
    }

    ticketCount.disabled = false;
    if (submitButton) submitButton.disabled = false;
    if (cartButton) cartButton.disabled = false;
    this.#syncAllTickets();
  }

  // --------------------------------------------------------------- cart add

  /**
   * Revalidates against the live cart, then adds.
   *
   * The revalidation is not paranoia. The rendered allowance is a snapshot,
   * and a product page can sit open while the same event is added in
   * another tab, or while the buyer uses the back button. Without a fresh
   * read, a buyer can push past available inventory through entirely
   * ordinary behaviour.
   *
   * This is UX enforcement, not a security control. Anyone willing to call
   * the cart API directly can exceed it, so nothing downstream may assume
   * the count held -- Flow and the roster treat ticket counts as observed,
   * never as guaranteed.
   *
   * @param {SommTicket[]} tickets
   * @param {'checkout' | 'cart'} [mode]
   */
  async #addToCart(tickets, mode = 'checkout') {
    const form = this.refs.form;
    if (!form) return;

    const variantId = Number(form.dataset.variantId);
    if (!variantId) return;

    this.#setSubmitting(true);

    try {
      const cartBefore = await this.#fetchCart();

      if (cartBefore) {
        const allowance = this.#computeAllowance(cartBefore);

        if (allowance <= 0) {
          this.#applyAllowance(0);
          this.#showFormError(
            'Your cart already holds every remaining ticket for this event. ' +
              'Please check out, or remove a ticket to make a change.'
          );
          return;
        }

        if (tickets.length > allowance) {
          this.#applyAllowance(allowance);
          this.#showFormError(
            `Your cart changed while this page was open. You can add ${allowance} more ` +
              `ticket${allowance === 1 ? '' : 's'} for this event. Please check the details and try again.`
          );
          return;
        }
      }

      // Generated once, so the same IDs can be checked for afterwards.
      const items = tickets.map((ticket) => ({
        id: variantId,
        quantity: 1,
        properties: this.#buildProperties(ticket),
      }));
      const expectedTicketIds = items.map((item) => item.properties._somm_ticket_id);

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      // Announced before the request, per the 4.1.3 contract. The promise is
      // settled below once the cart has been re-read, on both paths.
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: String(item.id),
            quantity: item.quantity,
          })),
          promise: deferredEventPromise.promise,
        })
      );

      let result;
      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });
        result = await response.json();
      } catch (error) {
        // A rejected promise leaves the cart UI in a defined state. Without
        // it the deferred promise never settles and anything awaiting it
        // hangs.
        deferredEventPromise.reject(error);
        this.#showFormError('Your tickets could not be added. Please try again.');
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        await this.#refreshAllowance();
        return;
      }

      if (result.status) {
        this.#showFormError(result.description || result.message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        const cartOnError = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartOnError),
          detail: {
            didError: true,
            items: cartOnError?.items ?? [],
            source: 'somm-attendee-form-component',
            sourceId: this.id.toString(),
            itemCount: 0,
            productId: form.dataset.productId,
          },
        });
        await this.#refreshAllowance();
        return;
      }

      // Confirm every ticket actually landed rather than trusting the add to
      // be all-or-nothing. A silent partial add is the worst outcome here:
      // the buyer pays believing everyone is registered, and a guest simply
      // is not on the door list.
      const cartAfter = await this.#fetchCart();
      const missing = this.#findMissingTickets(cartAfter, expectedTicketIds);

      if (missing.length > 0) {
        const added = expectedTicketIds.length - missing.length;
        this.#showFormError(
          `Only ${added} of ${expectedTicketIds.length} tickets were added. ` +
            'Please review your cart and add the missing guests again.'
        );
      }

      // The cart was already re-read above for the partial-add check, so
      // reuse it rather than fetching twice.
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartAfter),
        detail: {
          didError: missing.length > 0,
          items: cartAfter?.items ?? [],
          source: 'somm-attendee-form-component',
          sourceId: this.id.toString(),
          itemCount: items.length - missing.length,
          productId: form.dataset.productId,
          variantId: String(variantId),
          sections: result.sections,
        },
      });

      if (missing.length === 0) {
        const n = items.length;

        if (mode === 'checkout') {
          // Only after every ticket is confirmed in the cart. Anything else
          // already in the cart (wine for pickup) goes to checkout too.
          // Clear the guest fields first, so coming back with the browser's
          // back button does not invite adding the same guests twice.
          const message = `${n} ticket${n === 1 ? '' : 's'} in your cart. Taking you to checkout…`;
          this.#resetGuestFields();
          this.#showFormSuccess(message);
          this.#announce(message);
          this.#redirecting = true;
          window.location.assign('/checkout');
          return;
        }

        const message = `${n} ticket${n === 1 ? '' : 's'} added to your cart.`;
        this.#announce(message);
        this.#resetGuestFields();
        this.#showFormSuccess(message);
        this.#openCartDrawer();
      }

      if (cartAfter) this.#applyAllowance(this.#computeAllowance(cartAfter));
    } catch (error) {
      console.error(error);
      this.#showFormError('We could not add your tickets. Please try again.');
    } finally {
      // Keep the buttons disabled while the browser navigates to checkout,
      // so a second click cannot add the same guests twice.
      if (!this.#redirecting) this.#setSubmitting(false);
    }
  }

  /**
   * Returns the ticket IDs that were expected but are not in the cart.
   *
   * Returns an empty array when the cart could not be read -- an unreadable
   * cart is not evidence that anything failed, and claiming a partial add on
   * a failed lookup would send buyers chasing a problem that may not exist.
   *
   * @param {?{items: Array<Object>}} cart
   * @param {string[]} expectedTicketIds
   * @returns {string[]}
   */
  #findMissingTickets(cart, expectedTicketIds) {
    if (!cart?.items) return [];

    const present = new Set(
      cart.items
        .map((item) => item.properties?._somm_ticket_id)
        .filter(Boolean)
        .map(String)
    );

    return expectedTicketIds.filter((id) => !present.has(String(id)));
  }

  /** Re-reads the cart and updates the selectable allowance. */
  async #refreshAllowance() {
    const cart = await this.#fetchCart();
    if (cart) this.#applyAllowance(this.#computeAllowance(cart));
  }

  /**
   * Clears guest inputs after a successful add.
   *
   * Leaving the previous guest's details in place invites a buyer to hit add
   * twice and register the same person again without noticing.
   */
  #resetGuestFields() {
    for (const section of this.#ticketSections) {
      for (const field of section.querySelectorAll('input[type="text"], textarea')) {
        if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
          field.value = '';
        }
      }
    }
    this.#clearAllErrors();
    this.#syncAllTickets();
  }

  // ------------------------------------------------------------------ chrome

  /** @param {boolean} state */
  #setSubmitting(state) {
    this.#submitting = state;
    const { submitButton, cartButton } = this.refs;
    for (const button of [submitButton, cartButton]) {
      if (!button) continue;
      button.setAttribute('aria-disabled', String(state));
      button.disabled = state;
    }
  }

  /** @param {string} message */
  #showFormSuccess(message) {
    const { formSuccess } = this.refs;
    if (!formSuccess) return;
    formSuccess.textContent = message;
    formSuccess.hidden = false;
  }

  #clearFormSuccess() {
    const { formSuccess } = this.refs;
    if (!formSuccess) return;
    formSuccess.textContent = '';
    formSuccess.hidden = true;
  }

  /**
   * Opens Horizon's cart drawer when the store uses one. The drawer only
   * auto-opens on add when its auto-open setting is on, so open it directly.
   * On the cart-page layout there is no drawer and this is a no-op.
   */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }

  /** @param {string} message */
  #showFormError(message) {
    const { formError } = this.refs;
    if (!formError) return;
    formError.textContent = message;
    formError.hidden = false;
  }

  #clearFormError() {
    const { formError } = this.refs;
    if (!formError) return;
    formError.textContent = '';
    formError.hidden = true;
    this.#clearFormSuccess();
  }

  /** @param {string} message */
  #announce(message) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = message;
  }
}

customElements.define('somm-attendee-form-component', SommAttendeeForm);
